import type { Unit, UnitAbility } from "@sins2-atlas/game-data";
import { clsx } from "clsx";
import { useEffect, useId, useMemo, useRef } from "react";
import {
  type Allocation,
  abilityLevelRequirement,
  abilityPointPool,
  allocationAt,
  NO_POINTS,
  type PointedAbility,
  type PositionState,
  pointedAbilities,
  pointsSpent,
  positionState,
  setAbilityLevel,
} from "../lib/ability-points";
import { ABILITY_POINTS_POOL_CAPTION } from "../lib/disclosures";
import { honouredPoints, type StatFrame, withPoints } from "../lib/frame";
import { ABILITY_POINT_WORDS } from "../lib/frame-labels";
import { BASE_LEVEL } from "../lib/level";
import { ArtImage } from "./art-image";
import { keepPanelOnScreen, PanelAction, PickerPanel } from "./picker-panel";

// The Ability Points picker (Issue 62, ADR 0015 decision 4): the panel a reader spends one
// hull's points in. The Loadout picker's sibling — the same panel shell, the same transient
// open state (ADR 0002: the panel belongs to this visit, the allocation belongs to `?points=`)
// — and it differs where the lever does:
//
//  - a row is one of the hull's LEVEL-DRIVEN abilities, in the game's own order, and its
//    Ability Level positions 0..N are buttons rather than a tick: an Ability Level is a
//    position on a ladder, not a thing that is on or off;
//  - a position the hull's Level does not permit, or the pool cannot reach, is disabled WITH
//    ITS REASON on the position itself. The Loadout says a refusal in the panel's note, after
//    the click; here the reason belongs to one position among ten, so it is readable before
//    the click and by the keyboard (aria-disabled keeps the button focusable, and every
//    reader gets the same sentence through aria-describedby);
//  - the panel carries the pool's Assumption Caption, because how many points a Level gives
//    is the one assumed thing about this lever (ADR 0015's open gate).
//
// Every rule is lib/ability-points.ts's and every word is lib/frame-labels.ts's or
// lib/disclosures.ts's; nothing here decides either. The component takes a frame and a
// change handler rather than reaching for the page's, so a comparison column renders the
// same control over its own frame (Issue 63).

/** How a position is drawn, per state. `in-force` is the marked one; a disabled position
 * keeps a visible outline so the ladder still reads as a ladder with a hole in it. */
const POSITION_TONE: Record<PositionState, string> = {
  "in-force": "border-line-strong bg-surface font-semibold text-ink",
  available: "border-line text-ink-3 hover:border-line-strong hover:text-ink",
  gated: "border-line-faint text-muted-2",
  unaffordable: "border-line-faint text-muted-2",
};

function PositionButton({
  ability,
  position,
  state,
  onSet,
}: {
  ability: UnitAbility & PointedAbility;
  position: number;
  state: PositionState;
  onSet: () => void;
}) {
  // The reason's DOM id is this BUTTON's, not the ability's: two columns of one hull on the
  // comparison can both be open, and an id built from the ability alone would have the
  // second column's position describe itself with the first's sentence (review 2026-09-10).
  const reasonId = `${useId()}-reason`;
  const disabled = state === "gated" || state === "unaffordable";
  // The requirement the game states for this position; a gated position always has one, and
  // the fallback keeps the panel from printing a Level the game never named.
  const requires = abilityLevelRequirement(ability.requiredUnitLevels, position);
  const reason =
    state === "gated"
      ? requires === undefined
        ? undefined
        : ABILITY_POINT_WORDS.gated(requires)
      : state === "unaffordable"
        ? ABILITY_POINT_WORDS.unaffordable
        : undefined;
  return (
    <span className="inline-flex">
      <button
        type="button"
        data-ability-id={ability.id}
        data-position={position}
        data-position-state={state}
        aria-label={ABILITY_POINT_WORDS.position(ability.name, position)}
        aria-current={state === "in-force" ? "true" : undefined}
        // aria-disabled, never `disabled`: a disabled button is unreachable by Tab, and the
        // reason a position cannot be bought is exactly what a keyboard reader needs. The
        // click is refused here instead.
        aria-disabled={disabled ? "true" : undefined}
        aria-describedby={reason === undefined ? undefined : reasonId}
        title={reason ?? (state === "in-force" ? ABILITY_POINT_WORDS.inForceTitle : undefined)}
        onClick={() => {
          if (!disabled) onSet();
        }}
        // Never `outline-none`: in Tailwind v4 it sets --tw-outline-style: none and defeats
        // the focus-visible ring the keyboard path depends on.
        className={clsx(
          "min-w-6 rounded border px-1.5 py-px text-center font-mono text-[11.5px] [font-variant-numeric:tabular-nums] focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-link",
          POSITION_TONE[state],
          disabled ? "cursor-not-allowed" : "cursor-pointer",
        )}
      >
        {position}
      </button>
      {reason !== undefined && (
        <span id={reasonId} className="sr-only">
          {reason}
        </span>
      )}
    </span>
  );
}

function AbilityRow({
  ability,
  index,
  allocation,
  abilities,
  level,
  onSet,
}: {
  ability: UnitAbility & PointedAbility;
  index: number;
  allocation: Allocation;
  abilities: readonly PointedAbility[];
  level: number;
  onSet: (index: number, position: number) => void;
}) {
  // The positions the ability offers: 0 (Unspent) and one per Ability Level. Counted off the
  // ability's OWN gate list rather than its levelCount, because that list is what every rule
  // below reads and the two must not be able to disagree.
  const positions = Array.from({ length: ability.requiredUnitLevels.length + 1 }, (_, i) => i);
  const bought = allocationAt(allocation, index);
  return (
    <li className="flex items-center justify-between gap-3 py-1.5" data-ability-row={ability.id}>
      <span className="flex min-w-0 items-center gap-2">
        <ArtImage
          asset={ability.icon}
          alt=""
          className={clsx(
            "h-6 w-auto shrink-0 rounded border border-line-faint",
            bought === 0 && "opacity-45",
          )}
        />
        <span className={clsx("min-w-0 break-words text-[13.5px]", bought === 0 && "text-ink-3")}>
          {ability.name}
        </span>
      </span>
      {/* No wrapping group role: each position button's own accessible name already names
          the ability it belongs to, so a screen reader never meets a bare "2". */}
      <span className="flex shrink-0 items-center gap-1">
        {positions.map((position) => (
          <PositionButton
            key={position}
            ability={ability}
            position={position}
            state={positionState(allocation, index, position, abilities, level)}
            onSet={() => onSet(index, position)}
          />
        ))}
      </span>
    </li>
  );
}

/** The panel: the pool line, the pool rule's Assumption Caption, then one row per
 * level-driven ability in the game's order. It stays open while the reader spends (the Tech
 * and Loadout pickers' rule, not the single-select Level's): an allocation is several
 * choices, and a panel that shut on each of them would be reopened for the next.
 *
 * The allocation it draws is the one the page is SHOWING (honouredPoints) — a `?points=`
 * value this hull could not have bought folds nothing and is reported by the notice, so the
 * panel starts the reader from nothing spent rather than from a build the page is refusing. */
export function AbilityPointsPicker({
  panelId,
  unit,
  frame,
  onChange,
  onClose,
}: {
  /** The panel's own DOM id, wired to its trigger's aria-controls. */
  panelId: string;
  unit: Unit;
  frame: StatFrame;
  onChange: (next: StatFrame) => void;
  onClose: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  // The control sits last in the toolbar, so a left-anchored panel can hang past the viewport
  // at some widths. Nudge it back on open — the shell's one rule, shared with both pickers.
  useEffect(() => {
    if (panel.current !== null) keepPanelOnScreen(panel.current);
  }, []);

  // The level-driven abilities in the game's order, each still the whole UnitAbility (icon,
  // name) with its gates — which abilities the lever owns stays pointedAbilities' rule alone.
  const abilities = useMemo(() => pointedAbilities(unit.abilities), [unit.abilities]);
  const level = frame.level ?? BASE_LEVEL;
  const allocation = honouredPoints(unit, frame);
  const spent = pointsSpent(allocation);

  const setPosition = (index: number, position: number) => {
    const change = setAbilityLevel(allocation, index, position, abilities, level);
    if (change.refused !== undefined) return;
    onChange(withPoints(frame, change.allocation));
  };

  return (
    <PickerPanel
      panelRef={panel}
      panelId={panelId}
      width="w-[min(26rem,calc(100vw-1.5rem))]"
      summary={ABILITY_POINT_WORDS.poolLine(spent, abilityPointPool(level))}
      actions={
        <>
          <PanelAction onClick={() => onChange(withPoints(frame, NO_POINTS))}>
            {ABILITY_POINT_WORDS.unspendAll}
          </PanelAction>
          <PanelAction onClick={onClose}>{ABILITY_POINT_WORDS.close}</PanelAction>
        </>
      }
    >
      <p className="pb-1 text-[12px] text-muted-2">{ABILITY_POINTS_POOL_CAPTION}</p>
      <ul className="flex flex-col divide-y divide-line-faint">
        {abilities.map((ability, index) => (
          <AbilityRow
            key={ability.id}
            ability={ability}
            index={index}
            allocation={allocation}
            abilities={abilities}
            level={level}
            onSet={setPosition}
          />
        ))}
      </ul>
    </PickerPanel>
  );
}
